import { useState } from "react";
import { api } from "@/lib/api";
import { OurOffices } from "@/components/OurOffices";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Phone, Mail, MessageSquare, MapPin, Globe } from "lucide-react";
import { toast } from "sonner";

const INTERESTS = [
  "Buying Property", "Selling / Listing Property", "Booking Inspection",
  "Land Surveying", "Title Processing", "Construction Services",
  "Property Management", "Financing", "Diaspora Services", "Becoming an Agent",
];

export default function Contact() {
  const [form, setForm] = useState({ name: "", phone: "", email: "", interest: "", message: "" });
  const [submitting, setSubmitting] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.phone || !form.interest) {
      toast.error("Name, phone, and topic are required");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/inquiries", form);
      toast.success("Thanks! We'll be in touch shortly.");
      setForm({ name: "", phone: "", email: "", interest: "", message: "" });
    } catch {
      toast.error("Failed to send. Try WhatsApp instead.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <main className="max-w-7xl mx-auto px-6 md:px-12 py-16" data-testid="contact-page">
      <div className="grid lg:grid-cols-12 gap-12">
        <div className="lg:col-span-5">
          <div className="eyebrow mb-3">Talk to Us</div>
          <h1 className="font-display text-4xl md:text-5xl tracking-tighter">We&apos;re a phone call away.</h1>
          <p className="text-muted-foreground mt-4 leading-relaxed">
            Buying, selling, listing, title processing, surveying, construction, financing, diaspora services, or agent partnerships — let&apos;s talk.
          </p>

          <div className="mt-10 space-y-5">
            <ContactRow icon={Phone} label="Uganda / East Africa" value="+256 700 259 675 · +256 701 238 066 · +256 704 412 030" href="tel:+256700259675" tid="contact-ug" />
            <ContactRow icon={MessageSquare} label="North America · WhatsApp" value="+1 614 571 9333" href="https://wa.me/16145719333" tid="contact-wa" />
            <ContactRow icon={MessageSquare} label="North America · WhatsApp 2" value="+1 617 291 6821" href="https://wa.me/16172916821" tid="contact-wa-na2" />
            <ContactRow icon={Mail} label="Email" value="info@tigawaproperties.com" href="mailto:info@tigawaproperties.com" tid="contact-email" />
            <ContactRow icon={Globe} label="Website" value="www.tigawaproperties.com" href="https://www.tigawaproperties.com" tid="contact-website" />
            <ContactRow icon={MapPin} label="HQ" value="Kampala, Uganda" tid="contact-hq" />
          </div>

          <a
            href="https://wa.me/16145719333"
            target="_blank"
            rel="noreferrer"
            className="mt-10 inline-flex items-center gap-2 bg-primary text-primary-foreground rounded-full px-6 py-3 font-medium hover:bg-primary/90 transition-colors"
            data-testid="whatsapp-cta"
          >
            <MessageSquare className="w-4 h-4" /> Message on WhatsApp
          </a>
        </div>

        <form onSubmit={submit} className="lg:col-span-7 bg-card border border-border rounded-3xl p-8 md:p-10 space-y-4" data-testid="contact-form">
          <h2 className="font-display text-2xl">Send an inquiry</h2>
          <div className="grid md:grid-cols-2 gap-4">
            <Field label="Full name">
              <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="contact-name" />
            </Field>
            <Field label="Phone / WhatsApp">
              <Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} data-testid="contact-phone" />
            </Field>
          </div>
          <Field label="Email (optional)">
            <Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="contact-email-field" />
          </Field>
          <Field label="I'm interested in…">
            <Select value={form.interest} onValueChange={(v) => setForm({ ...form, interest: v })}>
              <SelectTrigger data-testid="contact-interest"><SelectValue placeholder="Choose a topic" /></SelectTrigger>
              <SelectContent>{INTERESTS.map((i) => <SelectItem key={i} value={i}>{i}</SelectItem>)}</SelectContent>
            </Select>
          </Field>
          <Field label="Tell us what you need">
            <Textarea rows={5} value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} placeholder="e.g., I'm looking for 1-2 acres titled land near Mukono with road access, budget UGX 60M…" data-testid="contact-message" />
          </Field>
          <Button type="submit" disabled={submitting} className="rounded-full bg-primary hover:bg-primary/90 h-12 px-8" data-testid="contact-submit">
            {submitting ? "Sending…" : "Submit inquiry"}
          </Button>
        </form>
      </div>

      <OurOffices />
    </main>
  );
}

function ContactRow({ icon: Icon, label, value, href, tid }) {
  const inner = (
    <div className="flex items-start gap-4 group">
      <div className="w-10 h-10 rounded-full bg-primary/10 grid place-items-center text-primary shrink-0">
        <Icon className="w-4 h-4" />
      </div>
      <div>
        <div className="text-[10px] tracking-[0.18em] uppercase font-bold text-muted-foreground">{label}</div>
        <div className="font-display text-lg font-semibold group-hover:text-primary transition-colors">{value}</div>
      </div>
    </div>
  );
  return href ? <a href={href} target={href.startsWith("http") ? "_blank" : undefined} rel="noreferrer" data-testid={tid}>{inner}</a> : <div data-testid={tid}>{inner}</div>;
}

function Field({ label, children }) {
  return (
    <div>
      <Label className="text-xs font-medium text-foreground/80">{label}</Label>
      <div className="mt-1.5">{children}</div>
    </div>
  );
}
